import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Clock, Save, Plus, X, Copy } from 'lucide-react';

const DAYS = [
  ['monday', 'Lunedì'], ['tuesday', 'Martedì'], ['wednesday', 'Mercoledì'],
  ['thursday', 'Giovedì'], ['friday', 'Venerdì'], ['saturday', 'Sabato'], ['sunday', 'Domenica'],
];

const GRID_FROM = 7 * 60;
const GRID_TO = 22 * 60;
const TICKS = [7, 9, 11, 13, 15, 17, 19, 21];

const toMin = (hhmm) => {
  const [h, m] = (hhmm || '0:0').split(':').map(Number);
  return h * 60 + m;
};
const pct = (minutes) => `${((minutes - GRID_FROM) / (GRID_TO - GRID_FROM)) * 100}%`;

/** Una barra verde per ogni fascia aperta: si vede subito com'è fatta la giornata. */
const DayBar = ({ day }) => {
  if (day.closed) {
    return <div className="h-2 w-full rounded-full bg-[repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(120,120,120,0.22)_5px,rgba(120,120,120,0.22)_10px)]" />;
  }
  const open = toMin(day.open);
  const close = toMin(day.close);
  const hasBreak = day.break_start && day.break_end;
  const spans = hasBreak
    ? [[open, toMin(day.break_start)], [toMin(day.break_end), close]]
    : [[open, close]];
  return (
    <div className="relative h-2 w-full rounded-full bg-muted/60">
      {spans.map(([from, to], i) => to > from && (
        <span
          key={i}
          className="absolute inset-y-0 rounded-full bg-emerald-400/70 dark:bg-emerald-500/60"
          style={{ left: pct(from), width: `${((to - from) / (GRID_TO - GRID_FROM)) * 100}%` }}
        />
      ))}
    </div>
  );
};

/** Orario settimanale del salone: regola la griglia dell'agenda e le prenotazioni online. */
export const BusinessHoursCard = () => {
  const { API, getAuthHeaders } = useAuth();
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/business-hours`, getAuthHeaders());
      setHours(res.data.hours);
    } catch {
      toast.error('Orari non caricati');
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const setDay = (day, patch) => setHours(prev => ({ ...prev, [day]: { ...prev[day], ...patch } }));

  const copyToOthers = (sourceKey) => {
    const source = hours[sourceKey];
    setHours(prev => {
      const next = { ...prev };
      DAYS.forEach(([key]) => {
        if (key !== sourceKey && !next[key].closed) {
          next[key] = { ...next[key], open: source.open, close: source.close,
            break_start: source.break_start, break_end: source.break_end };
        }
      });
      return next;
    });
    toast.success('Orario copiato sugli altri giorni aperti');
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/business-hours`, { hours }, getAuthHeaders());
      toast.success('Orari salvati: l’agenda è già aggiornata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  if (!hours) return null;

  const firstOpen = DAYS.find(([key]) => !hours[key].closed)?.[0];

  return (
    <Card data-testid="business-hours-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" />
          Orario settimanale
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Fuori da questi orari, e durante la pausa, l’agenda non accetta appuntamenti e la pagina prenotazioni
          non mostra posti liberi.
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-sm font-medium">Il salone è aperto</span>
          {firstOpen && (
            <button
              type="button"
              onClick={() => copyToOthers(firstOpen)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary transition-opacity hover:opacity-70"
              data-testid="business-hours-copy"
            >
              <Copy className="h-3.5 w-3.5" />
              Copia il {DAYS.find(([k]) => k === firstOpen)[1].toLowerCase()} sugli altri giorni aperti
            </button>
          )}
        </div>

        {/* Righello delle ore */}
        <div className="relative mb-1 hidden h-4 sm:block">
          <div className="absolute left-[9.5rem] right-0 top-0 h-4">
            {TICKS.map((h) => (
              <span key={h} className="absolute -translate-x-1/2 text-[11px] text-muted-foreground"
                style={{ left: pct(h * 60) }}>{h}</span>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          {DAYS.map(([key, label]) => {
            const day = hours[key];
            const hasBreak = Boolean(day.break_start || day.break_end);
            return (
              <div key={key} className="border-b border-border/60 py-2.5 last:border-0" data-testid={`business-hours-row-${key}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex w-[9.5rem] shrink-0 items-center gap-2.5">
                    <Switch
                      checked={!day.closed}
                      onCheckedChange={(v) => setDay(key, { closed: !v })}
                      data-testid={`business-hours-open-${key}`}
                    />
                    <span className={`text-sm font-medium ${day.closed ? 'text-muted-foreground' : ''}`}>{label}</span>
                  </div>

                  {day.closed ? (
                    <span className="text-sm text-muted-foreground">Chiuso</span>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <Input type="time" value={day.open} className="h-9 w-[8.25rem]"
                        onChange={(e) => setDay(key, { open: e.target.value })}
                        data-testid={`business-hours-from-${key}`} />
                      <span className="text-muted-foreground">–</span>
                      <Input type="time" value={day.close} className="h-9 w-[8.25rem]"
                        onChange={(e) => setDay(key, { close: e.target.value })}
                        data-testid={`business-hours-to-${key}`} />

                      {hasBreak ? (
                        <>
                          <span className="text-xs text-muted-foreground">pausa</span>
                          <Input type="time" value={day.break_start || ''} className="h-9 w-[8.25rem]"
                            onChange={(e) => setDay(key, { break_start: e.target.value })}
                            data-testid={`business-hours-break-from-${key}`} />
                          <span className="text-muted-foreground">–</span>
                          <Input type="time" value={day.break_end || ''} className="h-9 w-[8.25rem]"
                            onChange={(e) => setDay(key, { break_end: e.target.value })}
                            data-testid={`business-hours-break-to-${key}`} />
                          <button type="button" aria-label="Togli la pausa"
                            onClick={() => setDay(key, { break_start: null, break_end: null })}
                            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            data-testid={`business-hours-break-remove-${key}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : (
                        <button type="button"
                          onClick={() => setDay(key, { break_start: '13:00', break_end: '14:00' })}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-opacity hover:opacity-70"
                          data-testid={`business-hours-break-add-${key}`}>
                          <Plus className="h-3.5 w-3.5" />
                          Pausa
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-2 flex items-center">
                  <span className="hidden w-[9.5rem] shrink-0 sm:block" />
                  <DayBar day={day} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={save} disabled={saving} data-testid="business-hours-save">
            <Save className="mr-2 h-4 w-4" />
            Salva orari
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
